import type { KeyboardEvent } from 'react'
import { formatCurrency, formatDate } from '@dcp/shared'
import type { ProjectionRecord } from '@/lib/projection-cell'

/**
 * Accessible name of a saved row's selection checkbox. `facilityName` is given in
 * a pack cell, where two members can hold the same description; `null` otherwise.
 */
export function selectRowLabel(record: ProjectionRecord, facilityName: string | null): string {
  const at = facilityName === null ? '' : ` at ${facilityName}`
  const description = record.description?.trim()
  if (description) return `Select ${description}${at}`
  // Date and amount tell apart rows that have no description to name them by.
  const amount = formatCurrency(record.amount, { negativeStyle: 'parentheses' })
  return `Select projection of ${amount} on ${formatDate(record.date)}${at}`
}

/**
 * Row `onKeyDown` of a row in edit mode: Enter in a text field applies the row.
 * Only from an <input>: in an open Select or calendar (React bubbles through
 * portals) Enter picks an option.
 */
export function applyOnEnter(saving: boolean, onApply: () => void) {
  return (event: KeyboardEvent<HTMLTableRowElement>) => {
    if (event.key !== 'Enter' || event.nativeEvent.isComposing) return
    if (!(event.target instanceof HTMLInputElement) || saving) return
    event.preventDefault()
    onApply()
  }
}
